/**
 * Area of 2D Shapes generator (6.MGSR.1.1).
 *
 * Area of rectangles, triangles, parallelograms, and trapezoids. Dimensions are
 * chosen so the area is an exact, clean value (for triangle and trapezoid the
 * relevant product is kept even so the 1/2 factor divides evenly). Areas are
 * computed with the geometry helpers, so display and answer always agree.
 *
 * Families:
 *   F1: rectangle       (l * w)
 *   F2: triangle        ((1/2) b h)
 *   F3: parallelogram   (b * h)
 *   F4: trapezoid       ((1/2)(b1 + b2) h)
 */

import { rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import {
  areaRectangle,
  areaTriangle,
  areaParallelogram,
  areaTrapezoid,
} from "../engine/geometry";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "area-2d" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3" | "F4";

interface Cfg {
  readonly families: readonly Family[];
  readonly max: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F3"], max: 12 },
  standard: { families: ["F1", "F2", "F3"], max: 20 },
  hard: { families: ["F1", "F2", "F3", "F4"], max: 30 },
  challenge: { families: ["F2", "F4"], max: 40 },
};

/** An even integer in [2, max], so a 1/2 factor divides cleanly. */
function evenInt(rng: Rng, max: number): number {
  const hi = Math.floor(max / 2);
  return rng.int(1, hi) * 2;
}

export class Area2DGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      let prompt: string;
      let latex: string;
      let answer: ReturnType<typeof rat>;
      let stepDesc: string;

      switch (family) {
        case "F1": {
          const l = rng.int(2, cfg.max);
          const w = rng.int(2, cfg.max);
          answer = areaRectangle(rat(l), rat(w));
          prompt = `Find the area of a rectangle with length ${l} and width ${w}.`;
          latex = `A = l \\times w = ${l} \\times ${w} = ${answer.toLatex()}`;
          stepDesc = "Area of a rectangle is length times width.";
          break;
        }
        case "F2": {
          // Keep base even so (1/2) b h is a whole number.
          const b = evenInt(rng, cfg.max);
          const h = rng.int(2, cfg.max);
          answer = areaTriangle(rat(b), rat(h));
          prompt = `Find the area of a triangle with base ${b} and height ${h}.`;
          latex = `A = \\tfrac{1}{2} b h = \\tfrac{1}{2}(${b})(${h}) = ${answer.toLatex()}`;
          stepDesc = "Area of a triangle is one-half the base times the height.";
          break;
        }
        case "F3": {
          const b = rng.int(2, cfg.max);
          const h = rng.int(2, cfg.max);
          answer = areaParallelogram(rat(b), rat(h));
          prompt = `Find the area of a parallelogram with base ${b} and height ${h}.`;
          latex = `A = b h = ${b} \\times ${h} = ${answer.toLatex()}`;
          stepDesc = "Area of a parallelogram is base times height.";
          break;
        }
        case "F4": {
          const b1 = rng.int(2, cfg.max);
          const b2 = rng.int(2, cfg.max);
          // Keep height even so (1/2)(b1+b2)h is a whole number.
          const h = evenInt(rng, cfg.max);
          answer = areaTrapezoid(rat(b1), rat(b2), rat(h));
          prompt = `Find the area of a trapezoid with parallel bases ${b1} and ${b2} and height ${h}.`;
          latex = `A = \\tfrac{1}{2}(b_1 + b_2) h = \\tfrac{1}{2}(${b1} + ${b2})(${h}) = ${answer.toLatex()}`;
          stepDesc = "Area of a trapezoid is one-half the sum of the parallel bases times the height.";
          break;
        }
      }

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: prompt },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps: [{ description: stepDesc, latex }],
        hints: [
          { level: 1, text: "Pick the correct area formula for the shape." },
          { level: 2, text: "Substitute the given lengths, then simplify." },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const area2d = new Area2DGenerator();
